import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Check } from "lucide-react";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import styles from "./EmailCaptureView.module.css";
import Heading from "../Heading";
import Button from "../Button";
import { useAppContext } from "../../context/AppContext";
import { useBrand } from "../../context/BrandContext";
import { useCopy } from "../../context/CopyContext";

interface SettingsModalViewProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsModalView: React.FC<SettingsModalViewProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { isDarkMode, toggleDarkMode } = useBrand();
  const { getCopy } = useCopy();
  const [pin, setPin] = useState(["", "", "", ""]);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [layoutName, setLayoutName] = useState("default");
  
  const keyboardRef = useRef<any>(null);
  const keyboardContainerRef = useRef<HTMLDivElement>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const correctPin = "1234";

  // Toggle Button Component
  interface ToggleButtonProps {
    label: string;
    isActive: boolean;
    onClick: () => void;
    size?: 'small' | 'medium' | 'large';
    disabled?: boolean;
  }

  const ToggleButton: React.FC<ToggleButtonProps> = ({ label, isActive, onClick, size = 'small', disabled = false }) => (
    <Button
      variant={isActive ? "primary" : "secondary"}
      size={size}
      onClick={onClick}
      className="flex-1"
      icon={isActive ? <Check size={16} strokeWidth={2} /> : undefined}
      iconPosition="left"
      disabled={disabled}
    >
      {label}
    </Button>
  );

  // Focus first field and show keyboard when modal opens
  useEffect(() => {
    if (isOpen && !isSuccess) {
      setShowKeyboard(true);
      setCurrentFieldIndex(0);
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [isOpen, isSuccess]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPin(["", "", "", ""]);
      setCurrentFieldIndex(0);
      setShowKeyboard(false);
      setIsError(false);
      setIsSuccess(false);
    }
  }, [isOpen]);

  const handlePinChange = (index: number, value: string) => {
    // Only allow single digits
    if (value.length > 1) return;
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setIsError(false);

    // Update keyboard input to show current PIN
    const pinString = newPin.join("");
    if (keyboardRef.current) {
      keyboardRef.current.setInput(pinString);
    }

    if (value) {
      // Auto-advance to next field
      if (index < 3) {
        const nextIndex = index + 1;
        setCurrentFieldIndex(nextIndex);
        setTimeout(() => {
          inputRefs.current[nextIndex]?.focus();
        }, 50);
      } else {
        // All fields filled, check if we actually have 4 digits before validating
        const filledPin = newPin.filter(digit => digit !== "");
        if (filledPin.length === 4) {
          setTimeout(() => {
            validatePin([...newPin]);
          }, 100);
        }
      }
    }
  };

  const validatePin = (pinArray: string[]) => {
    const enteredPin = pinArray.join("");
    
    // Only validate if we actually have 4 digits
    if (enteredPin.length !== 4) {
      return;
    }
    
    if (enteredPin === correctPin) {
      setIsSuccess(true);
      setShowKeyboard(false);
    } else {
      setIsError(true);
      // Clear PIN after error and reset focus
      setTimeout(() => {
        // Complete state reset
        setPin(["", "", "", ""]);
        setCurrentFieldIndex(0);
        setIsError(false);
        
        // Clear keyboard input as well
        if (keyboardRef.current) {
          keyboardRef.current.setInput("");
        }
        
        // Focus first input after clearing
        setTimeout(() => {
          inputRefs.current[0]?.focus();
        }, 100);
      }, 1500);
    }
  };

  const onKeyboardChange = (input: string) => {
    // Update PIN fields based on keyboard input
    const digits = input.slice(0, 4).split("");
    const newPin = ["", "", "", ""];
    
    digits.forEach((digit, index) => {
      if (index < 4 && /^\d$/.test(digit)) {
        newPin[index] = digit;
      }
    });

    setPin(newPin);
    setIsError(false); // Clear error when user starts typing again
    
    // Update current field index
    const filledCount = newPin.filter(d => d !== "").length;
    if (filledCount < 4) {
      setCurrentFieldIndex(filledCount);
      setTimeout(() => {
        inputRefs.current[filledCount]?.focus();
      }, 50);
    } else {
      // All fields filled, double-check we have exactly 4 valid digits before validating
      if (filledCount === 4 && newPin.every(digit => digit !== "")) {
        setTimeout(() => {
          validatePin(newPin);
        }, 100);
      }
    }
  };

  const onKeyPress = (button: string) => {
    if (button === "{shift}" || button === "{lock}") {
      setLayoutName(layoutName === "default" ? "shift" : "default");
    }
    
    if (button === "{bksp}") {
      // Handle backspace
      const currentPin = [...pin];
      let indexToUpdate = currentFieldIndex;
      
      if (currentPin[currentFieldIndex] === "" && currentFieldIndex > 0) {
        indexToUpdate = currentFieldIndex - 1;
      }
      
      currentPin[indexToUpdate] = "";
      setPin(currentPin);
      setCurrentFieldIndex(indexToUpdate);
      
      setTimeout(() => {
        inputRefs.current[indexToUpdate]?.focus();
      }, 50);
    }
    
    if (button === "{enter}") {
      // Submit current PIN if all fields are filled
      const filledCount = pin.filter(digit => digit !== "").length;
      if (filledCount === 4 && pin.every(digit => digit !== "")) {
        validatePin(pin);
      }
    }
  };

  const handleFieldFocus = (index: number) => {
    setCurrentFieldIndex(index);
    if (!showKeyboard) {
      setShowKeyboard(true);
    }
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    
    // Check if click is outside keyboard area
    const isOutsideKeyboard = keyboardContainerRef.current && !keyboardContainerRef.current.contains(target);
    
    if (showKeyboard && isOutsideKeyboard && !isSuccess) {
      // Don't close keyboard in PIN entry mode - keep it open
      return;
    }
  }, [showKeyboard, isSuccess]);

  useEffect(() => {
    if (showKeyboard && !isSuccess) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showKeyboard, isSuccess, handleClickOutside]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ 
            duration: isOpen ? 0.3 : 0.3,
            ease: [0.25, 0.1, 0.25, 1]
          }}
        >
          {/* Background Overlay */}
          <motion.div
            className="absolute inset-0 backdrop-blur-lg"
            style={{ 
              backgroundColor: isDarkMode 
                ? 'rgba(23, 23, 23, 0.6)' 
                : 'rgba(250, 250, 250, 0.6)' 
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: isOpen ? 0.4 : 0.3,
              ease: [0.25, 0.1, 0.25, 1]
            }}
            onClick={onClose}
          />

          {/* Container that hugs both elements */}
          <div className="flex flex-col items-center max-w-[920px] w-full h-full">
            {/* Height Container - fills available space minus close button */}
            <div className="flex-1 w-full flex flex-col mb-8">
              {/* Main Container */}
              <motion.div
                className="relative rounded-[80px] w-full overflow-hidden"
                style={{
                  background: `linear-gradient(180deg, var(--brand-background) 80.05%, var(--brand-surface) 100%)`,
                  boxShadow: isDarkMode 
                    ? `0 20px 40px 0 rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)`
                    : `0 20px 40px 0 rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)`
                }}
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={
                  isOpen 
                    ? {
                        type: "spring",
                        damping: 30,
                        stiffness: 300,
                        mass: 1
                      }
                    : {
                        duration: 0.3,
                        ease: [0.25, 0.1, 0.25, 1]
                      }
                }
              >
                {/* Content Container */}
                <div className="p-20 pt-28 pb-32">
                  <motion.div
                    className="flex flex-col gap-20 w-full max-w-[760px] mx-auto"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={
                      isOpen 
                        ? {
                            duration: 0.4,
                            ease: [0.25, 0.1, 0.25, 1]
                          }
                        : {
                            duration: 0.2,
                            ease: [0.25, 0.1, 0.25, 1]
                          }
                    }
                  >
                    {!isSuccess ? (
                      <>
                        {/* Header Section */}
                        <Heading 
                          title={getCopy('settingsModal.heading')} 
                          subtitle={getCopy('settingsModal.subheading')} 
                        />

                        {/* PIN Input Section */}
                        <div className="flex flex-col items-center gap-8">
                          <div className="flex w-[600px] items-start gap-10">
                            {pin.map((digit, index) => (
                              <motion.input
                                key={index}
                                ref={el => { inputRefs.current[index] = el; }}
                                type="password"
                                inputMode="numeric"
                                value={digit}
                                onChange={(e) => handlePinChange(index, e.target.value)}
                                onFocus={() => handleFieldFocus(index)}
                                className={`w-[120px] h-[140px] text-center text-2xl font-medium rounded-[24px] bg-brand-background text-brand-text-primary focus:outline-none transition-colors duration-200 font-brand-secondary ${
                                  isError 
                                    ? "border border-red-500 bg-red-50 text-red-600" 
                                    : currentFieldIndex === index
                                      ? "border border-brand-primary" 
                                      : "border border-brand-border"
                                }`}
                                style={{ fontFamily: 'var(--brand-font-secondary)' }}
                                maxLength={1}
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                              />
                            ))}
                          </div>

                          {isError && (
                            <motion.p
                              className="text-red-500 text-center text-[18px] font-brand-secondary"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              {getCopy('settingsModal.pinError')}
                            </motion.p>
                          )}
                        </div>
                      </>
                    ) : (
                      /* Management Interface */
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 200,
                          damping: 20
                        }}
                      >
                        <Heading 
                          title={getCopy('settingsModal.managementHeading')} 
                          subtitle={getCopy('settingsModal.managementSubheading')} 
                        />
                        
                        {/* Settings Options Grid */}
                        <div className="grid grid-cols-2 gap-6 mt-24">
                          <Button
                            variant="secondary"
                            size="medium"
                            onClick={() => {
                              dispatch({ type: 'ACCEPT_UNIDENTIFIED_EMPTY' });
                              onClose();
                            }}
                          >
                            {getCopy('settingsModal.acceptUnidentifiedEmpty')}
                          </Button>
                          
                          <ToggleButton
                            label={getCopy('settingsModal.enableTestMode')}
                            size="medium"
                            isActive={state.settings.testMode}
                            onClick={() => dispatch({ type: 'TOGGLE_TEST_MODE' })}
                          />
                          
                          <ToggleButton
                            label={getCopy('settingsModal.enableInlineSignUp')}
                            size="medium"
                            isActive={state.settings.inlineSignUp}
                            onClick={() => dispatch({ type: 'TOGGLE_INLINE_SIGN_UP' })}
                          />
                          
                          <ToggleButton
                            label={getCopy('settingsModal.enableInlineSignIn')}
                            size="medium"
                            isActive={state.settings.inlineSignIn}
                            onClick={() => dispatch({ type: 'TOGGLE_INLINE_SIGN_IN' })}
                          />
                          
                          <ToggleButton
                            label={getCopy('settingsModal.showAdaGuidelines')}
                            size="medium"
                            isActive={state.settings.adaGuidelines}
                            onClick={() => dispatch({ type: 'TOGGLE_ADA_GUIDELINES' })}
                          />
                          
                          <ToggleButton
                            label={getCopy('settingsModal.darkMode')}
                            size="medium"
                            isActive={isDarkMode}
                            onClick={toggleDarkMode}
                          />
                          
                          <ToggleButton
                            label={getCopy('settingsModal.demoMode')}
                            size="medium"
                            isActive={state.settings.demoMode}
                            onClick={() => dispatch({ type: 'TOGGLE_DEMO_MODE' })}
                            disabled={!state.settings.productReviewAndScan}
                          />
                          
                          <ToggleButton
                            label={getCopy('settingsModal.enableProductReviewAndScan')}
                            size="medium"
                            isActive={state.settings.productReviewAndScan}
                            onClick={() => {
                              dispatch({ type: 'TOGGLE_PRODUCT_REVIEW_AND_SCAN' });
                              // If disabling scan mode, also disable demo mode
                              if (state.settings.productReviewAndScan && state.settings.demoMode) {
                                dispatch({ type: 'TOGGLE_DEMO_MODE' });
                              }
                            }}
                          />
                          
                          <Button
                            variant="tertiary"
                            size="medium"
                            disabled
                          >
                            {getCopy('settingsModal.editCustomerEmail')}
                          </Button>
                          
                          <Button
                            variant="tertiary"
                            size="medium"
                            disabled
                          >
                            {getCopy('settingsModal.resendRewardEmail')}
                          </Button>
                          
                          <Button
                            variant="secondary"
                            size="medium"
                            onClick={() => {
                              // Reset all state except settings
                              dispatch({ type: 'RESET' });
                              // Close the settings modal
                              onClose();
                              // Navigate back to the start
                              navigate('/');
                            }}
                          >
                            {getCopy('settingsModal.resetTradeIn')}
                          </Button>
                          
                          <Button
                            variant="tertiary"
                            size="medium"
                            disabled
                          >
                            {getCopy('settingsModal.manageTradeIn')}
                          </Button>

                          <Button
                            variant="tertiary"
                            size="medium"
                            disabled
                          >
                            {getCopy('settingsModal.sendTcEmail')}
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Close Button - Positioned at bottom of hugging container or above keyboard */}
            <motion.div
              className={`flex justify-center z-10 ${showKeyboard && !isSuccess ? 'fixed bottom-[400px] left-1/2 transform -translate-x-1/2' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={
                isOpen 
                  ? {
                      duration: 0.3,
                      ease: [0.25, 0.1, 0.25, 1]
                    }
                  : {
                      duration: 0.2,
                      ease: [0.25, 0.1, 0.25, 1]
                    }
              }
            >
              <motion.button
                onClick={onClose}
                className="bg-brand-text-primary/10 rounded-full w-[88px] h-[88px] flex items-center justify-center hover:bg-brand-text-primary/20 transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <X size={40} stroke="var(--brand-text-secondary)" strokeWidth={1.5} />
              </motion.button>
            </motion.div>
          </div>

          {/* Virtual Keyboard - Only show during PIN entry */}
          <AnimatePresence>
            {showKeyboard && !isSuccess && (
              <motion.div
                className="fixed bottom-0 left-0 right-0 z-50 bg-brand-background"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{
                  duration: 0.2,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                ref={keyboardContainerRef}
              >
                <div className={styles.keyboardContainer}>
                  <Keyboard
                    ref={keyboardRef}
                    layoutName={layoutName}
                    onChange={onKeyboardChange}
                    onKeyPress={onKeyPress}
                    theme="hg-theme-default hg-layout-default"
                    layout={{
                      default: [
                        "1 2 3",
                        "4 5 6", 
                        "7 8 9",
                        "{bksp} 0 {enter}"
                      ]
                    }}
                    display={{
                      '{bksp}': '⌫',
                      '{enter}': '✓'
                    }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SettingsModalView;
